/**
 * Contexto de gestión de proveedores de tracking vehicular
 * Implementa arquitectura de estado centralizada con múltiples proveedores y eventos en tiempo real
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useSocket } from "./SocketContext";
import { useNotification, NotificationSeverity } from "./NotificationContext";

const TrackingProvidersContext = createContext(null);

/**
 * Hook personalizado para acceder al contexto de tracking
 * @returns {Object} Contexto de tracking vehicular
 * @throws {Error} Si se usa fuera del proveedor
 */
export const useTrackingProviders = () => {
  const context = useContext(TrackingProvidersContext);
  if (!context) {
    throw new Error('useTrackingProviders debe ser usado dentro de TrackingProvidersProvider');
  }
  return context;
};

/**
 * Configuración de proveedores disponibles en el sistema
 */
const AVAILABLE_PROVIDERS = [
  { id: 'all', name: 'Todos los Proveedores' },
  { id: 'securita', name: 'Securita' },
  { id: 'kontigo', name: 'Kontigo' },
  { id: 'tecnosur', name: 'Tecnosur' },
  { id: 'coltrack', name: 'Coltrack' },
  { id: 'protrack365', name: 'Protrack365' },
  { id: 'satelitales', name: 'Satelitales' },
  { id: 'cellvi', name: 'Cellvi' }
];

/**
 * Proveedor de contexto para gestión de tracking vehicular
 * @param {Object} props - Props del componente
 * @param {React.ReactNode} props.children - Componentes hijos
 */
export const TrackingProvidersProvider = ({ children }) => {
  // Estados principales del sistema
  const [vehiclePositions, setVehiclePositions] = useState(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [trackedEvents, setTrackedEvents] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [isInitialized, setIsInitialized] = useState(false);
  
  /**
   * Gestión de proveedor activo con persistencia en localStorage
   */
  const [activeProvider, setActiveProvider] = useState(() => {
    const saved = localStorage.getItem('selectedTrackingProvider');
    return saved ? JSON.parse(saved) : AVAILABLE_PROVIDERS[0];
  });
  
  // Referencias para optimización de rendimiento
  const subscriptionsRef = useRef(new Set());
  const initializationTimeout = useRef(null);
  const isFirstLoad = useRef(true);
  
  // Hooks de contexto externos
  const socket = useSocket();
  const { showNotification } = useNotification();

  /**
   * Solicita datos iniciales específicos del proveedor seleccionado
   * @param {string|null} providerId - ID del proveedor o null para usar el activo
   */
  const requestInitialData = useCallback((providerId = null) => {
    if (!socket) return;

    const targetProvider = providerId || activeProvider.id;
    
    // Solicitar estado inicial general del sistema
    socket.emit('vehicle:request_initial_state', {
      timestamp: Date.now(),
      clientId: socket.id,
      provider: targetProvider !== 'all' ? targetProvider : null,
      forceRefresh: isFirstLoad.current
    });

    // Solicitar datos específicos del proveedor si no es 'all'
    if (targetProvider !== 'all') {
      socket.emit(`${targetProvider}:request_positions`, {
        timestamp: Date.now(),
        clientId: socket.id
      });
    }
  }, [socket, activeProvider.id]);

  /**
   * Configuración de suscripciones WebSocket y gestión de inicialización
   */
  useEffect(() => {
    if (!socket) {
      setConnectionStatus('disconnected');
      setIsConnected(false);
      setIsInitialized(false);
      return;
    }

    setConnectionStatus('connecting');

    // Limpiar timeout previo de inicialización
    if (initializationTimeout.current) {
      clearTimeout(initializationTimeout.current);
    }

    /**
     * Procesador de posiciones de todos los vehículos
     * @param {Array} positions - Array de posiciones vehiculares
     */
    const handleAllPositions = (positions) => {
      try {
        const positionsMap = new Map();
        const duplicatesFound = new Set();
        
        positions.forEach(position => {
          const vehicleKey = generateVehicleKey(position);
          if (vehicleKey) {
            if (positionsMap.has(vehicleKey)) {
              duplicatesFound.add(vehicleKey);
              const existing = positionsMap.get(vehicleKey);
              const existingDate = new Date(existing.ReportDate || existing.lastUpdate || 0);
              const currentDate = new Date(position.ReportDate || 0);
              
              // Mantener posición más reciente basada en timestamp
              if (currentDate >= existingDate) {
                positionsMap.set(vehicleKey, {
                  ...position,
                  lastUpdate: new Date().toISOString(),
                  provider: position.Provider || 'unknown'
                });
              }
            } else {
              positionsMap.set(vehicleKey, {
                ...position,
                lastUpdate: new Date().toISOString(),
                provider: position.Provider || 'unknown'
              });
            }
          }
        });
        
        setVehiclePositions(positionsMap);
        setLastUpdate(new Date().toISOString());
        setIsConnected(true);
        setConnectionStatus('connected');
        setIsInitialized(true);
        
      } catch (error) {
        console.error('Error procesando posiciones:', error);
      }
    };

    /**
     * Factory para manejadores de posiciones por proveedor específico
     * @param {string} providerName - Nombre del proveedor
     * @returns {Function} Manejador de posiciones
     */
    const handleProviderPositions = (providerName) => (positions) => {
      try {
        setVehiclePositions(prevPositions => {
          const newPositions = new Map(prevPositions);
          
          positions.forEach(position => {
            const vehicleKey = generateVehicleKey(position, providerName);
            if (vehicleKey) {
              newPositions.set(vehicleKey, {
                ...position,
                lastUpdate: new Date().toISOString(),
                provider: providerName
              });
            }
          });
          
          return newPositions;
        });
        
        setLastUpdate(new Date().toISOString());
        setIsConnected(true);
        setConnectionStatus('connected');
        setIsInitialized(true);
        
      } catch (error) {
        console.error(`Error procesando ${providerName}:`, error);
      }
    };

    /**
     * Procesador de eventos vehiculares en tiempo real
     * @param {Object} eventData - Datos del evento vehicular
     */
    const handleVehicleEvent = (eventData) => {
      try {
        const newEvent = {
          ...eventData,
          id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          receivedAt: new Date().toISOString()
        };
        
        // Mantener solo los últimos 100 eventos para optimización de memoria
        setTrackedEvents(prevEvents => {
          const updatedEvents = [newEvent, ...prevEvents].slice(0, 100);
          return updatedEvents;
        });
        
        // Notificación para vehículos configurados
        if (eventData.isConfiguredVehicle) {
          showNotification({
            message: `🚨 ${eventData.details?.eventTypeName || eventData.type}: ${eventData.vehicleId}`,
            severity: NotificationSeverity.WARNING,
            autoHideDuration: 8000
          });
        }
      } catch (error) {
        console.error('Error procesando evento de vehículo:', error);
      }
    };

    /**
     * Manejador de estado de conexión del tracking
     * @param {Object} status - Estado de conexión
     */
    const handleTrackingStatus = (status) => {
      setConnectionStatus(status.connected ? 'connected' : 'disconnected');
      setIsConnected(status.connected);
      
      // Solicitar datos cuando se reconecta y no está inicializado
      if (status.connected && !isInitialized) {
        requestInitialData();
      }
    };

    /**
     * Confirmación de inicialización completada
     * @param {Object} data - Datos de inicialización
     */
    const handleInitializationComplete = (data) => {
      setIsInitialized(true);
    };

    // Configuración de listeners de eventos WebSocket
    const eventListeners = [
      ['vehicles:positions', handleAllPositions],
      ['vehicle:tracking:status', handleTrackingStatus],
      ['vehicle:event', handleVehicleEvent],
      ['vehicle:initialization:complete', handleInitializationComplete],
      
      // Listeners específicos por proveedor
      ['securita:vehicles:positions', handleProviderPositions('securita')],
      ['kontigo:vehicles:positions', handleProviderPositions('kontigo')],
      ['tecnosur:vehicles:positions', handleProviderPositions('tecnosur')],
      ['coltrack:vehicles:positions', handleProviderPositions('coltrack')],
      ['protrack365:vehicles:positions', handleProviderPositions('protrack365')],
      ['satelitales:vehicles:positions', handleProviderPositions('satelitales')],
      ['cellvi:vehicles:positions', handleProviderPositions('cellvi')]
    ];

    // Registro de todos los listeners
    eventListeners.forEach(([event, handler]) => {
      socket.on(event, handler);
      subscriptionsRef.current.add(event);
    });

    /**
     * Proceso de inicialización con timeout de seguridad
     */
    const initializeData = () => {
      requestInitialData();
      
      // Timeout de seguridad para reintentar inicialización
      initializationTimeout.current = setTimeout(() => {
        if (!isInitialized) {
          requestInitialData();
        }
      }, 5000);
    };

    initializeData();

    // Cleanup de listeners y timeouts
    return () => {
      eventListeners.forEach(([event, handler]) => {
        socket.off(event, handler);
      });
      subscriptionsRef.current.clear();
      
      if (initializationTimeout.current) {
        clearTimeout(initializationTimeout.current);
      }
    };
  }, [socket, showNotification, requestInitialData, isInitialized]);

  /**
   * Efecto para manejar cambios de proveedor activo
   */
  useEffect(() => {
    if (socket && isConnected && activeProvider) {
      // Marcar como no inicializado para forzar recarga de datos
      setIsInitialized(false);
      
      // Solicitar datos del nuevo proveedor con delay mínimo
      setTimeout(() => {
        requestInitialData(activeProvider.id);
      }, 100);
    }
  }, [activeProvider, socket, isConnected, requestInitialData]);

  /**
   * Generador de clave única para identificación de vehículos
   * @param {Object} vehicle - Objeto de vehículo
   * @param {string|null} providerName - Nombre del proveedor
   * @returns {string|null} Clave única del vehículo
   */
  const generateVehicleKey = useCallback((vehicle, providerName = null) => {
    const provider = providerName || vehicle.Provider || 'unknown';
    const id = vehicle.UnitPlate || vehicle.UnitId || vehicle.GpsIdentif || vehicle.Placa;
    
    if (!id) return null;
    return `${provider}_${id}`;
  }, []);

  /**
   * Cambio de proveedor activo con persistencia y recarga de datos
   * @param {string} providerId - ID del nuevo proveedor
   */
  const changeProvider = useCallback((providerId) => {
    const newProvider = AVAILABLE_PROVIDERS.find(p => p.id === providerId);
    if (newProvider && newProvider.id !== activeProvider.id) {
      setActiveProvider(newProvider);
      localStorage.setItem('selectedTrackingProvider', JSON.stringify(newProvider));
      
      // Resetear estado de inicialización para forzar recarga
      setIsInitialized(false);
    }
  }, [activeProvider.id]);

  /**
   * Selección de vehículo específico
   * @param {Object} vehicle - Objeto de vehículo a seleccionar
   */
  const selectVehicle = useCallback((vehicle) => {
    setSelectedVehicle(vehicle);
  }, []);

  /**
   * Limpiar selección de vehículo
   */
  const clearSelection = useCallback(() => {
    setSelectedVehicle(null);
  }, []);

  /**
   * Forzar actualización manual de datos
   */
  const forceUpdate = useCallback(async () => {
    if (!socket) {
      showNotification({
        message: 'No hay conexión con el servidor',
        severity: NotificationSeverity.ERROR
      });
      return;
    }

    try {
      setIsInitialized(false);
      requestInitialData();
      
      showNotification({
        message: 'Solicitando actualización de posiciones...',
        severity: NotificationSeverity.INFO
      });
    } catch (error) {
      showNotification({
        message: `Error al forzar actualización: ${error.message}`,
        severity: NotificationSeverity.ERROR
      });
    }
  }, [socket, showNotification, requestInitialData]);

  /**
   * Obtener vehículos filtrados por proveedor
   * @param {string} providerId - ID del proveedor
   * @returns {Array} Array de vehículos del proveedor
   */
  const getVehiclesByProvider = useCallback((providerId) => {
    if (providerId === 'all') {
      return Array.from(vehiclePositions.values());
    }
    
    return Array.from(vehiclePositions.values()).filter(
      vehicle => vehicle.provider === providerId
    );
  }, [vehiclePositions]);

  /**
   * Buscar vehículo específico por ID y proveedor
   * @param {string} vehicleId - ID del vehículo
   * @param {string|null} providerId - ID del proveedor (opcional)
   * @returns {Object|null} Objeto de vehículo encontrado o null
   */
  const findVehicle = useCallback((vehicleId, providerId = null) => {
    if (providerId) {
      const key = `${providerId}_${vehicleId}`;
      return vehiclePositions.get(key) || null;
    }
    
    // Búsqueda en todos los proveedores
    for (const [key, vehicle] of vehiclePositions) {
      if (key.endsWith(`_${vehicleId}`)) {
        return vehicle;
      }
    }
    
    return null;
  }, [vehiclePositions]);

  /**
   * Generar estadísticas completas del sistema de tracking
   * @returns {Object} Estadísticas detalladas por proveedor y globales
   */
  const getTrackingStats = useCallback(() => {
    const providerStats = {};
    let totalVehicles = 0;
    let activeVehicles = 0;
    
    for (const vehicle of vehiclePositions.values()) {
      const provider = vehicle.provider || 'unknown';
      
      if (!providerStats[provider]) {
        providerStats[provider] = { total: 0, active: 0 };
      }
      
      providerStats[provider].total++;
      totalVehicles++;
      
      // Determinar si el vehículo está activo
      const isActive = (vehicle.GpsSpeed && parseFloat(vehicle.GpsSpeed) > 0) || 
                      vehicle.Ignition === 1;
      
      if (isActive) {
        providerStats[provider].active++;
        activeVehicles++;
      }
    }
    
    return {
      totalVehicles,
      activeVehicles,
      providerStats,
      lastUpdate,
      connectionStatus,
      eventsCount: trackedEvents.length
    };
  }, [vehiclePositions, lastUpdate, connectionStatus, trackedEvents.length]);

  // Marcar primera carga como completada
  useEffect(() => {
    if (isInitialized && isFirstLoad.current) {
      isFirstLoad.current = false;
    }
  }, [isInitialized]);

  /**
   * Valor del contexto con todas las funcionalidades disponibles
   */
  const contextValue = {
    // Configuración de proveedores
    providers: AVAILABLE_PROVIDERS,
    activeProvider,
    changeProvider,
    
    // Estado de vehículos y conexión
    vehicles: Array.from(vehiclePositions.values()),
    vehiclePositions,
    isConnected,
    connectionStatus,
    lastUpdate,
    selectedVehicle,
    trackedEvents,
    isInitialized,
    
    // Funciones de gestión
    selectVehicle,
    clearSelection,
    forceUpdate,
    requestInitialData,
    
    // Utilidades de consulta
    getVehiclesByProvider,
    findVehicle,
    getTrackingStats,
    
    // Métricas y estado
    totalVehicles: vehiclePositions.size,
    isLoading: connectionStatus === 'connecting' || !isInitialized,
    isError: connectionStatus === 'error',
    
    // Funciones de utilidad
    isProviderActive: (providerId) => activeProvider?.id === providerId,
    getProviderById: (providerId) => AVAILABLE_PROVIDERS.find(p => p.id === providerId)
  };

  return (
    <TrackingProvidersContext.Provider value={contextValue}>
      {children}
    </TrackingProvidersContext.Provider>
  );
};

TrackingProvidersProvider.propTypes = {
  children: PropTypes.node.isRequired,
};